import { USER_ROLE_PRODUCER } from "@datanac/datanac-api-toolkit/dist/api-helper";
import {
  ArcPlcIcon,
  BrokerIcon,
  BudgetIcon,
  CalculatorIcon,
  FarmVaultIcon,
  FinancialIcon,
  InsuranceIcon,
  LoanOriginationIcon,
  MarketingIcon,
  MemberManagementIcon,
  ReportsIcon,
  SecurityIcon,
  SystemIcon,
  TilleyIcon,
} from "components/Icons/Icons";
import { USER_ROLE_ADMIN, USER_ROLE_AGENT } from "core/useSession";

import {
  MODULE_ARC_PLC,
  MODULE_BROKER,
  MODULE_BUDGET,
  MODULE_CALCULATORS,
  MODULE_FARM_VAULT,
  MODULE_FINANCIAL,
  MODULE_INSURANCE,
  MODULE_MARKETING,
  MODULE_REPORTS,
  MODULE_SYSTEM,
  MODULE_TILLEY,
} from "./constants";

export const menuConfig = [
  {
    id: MODULE_FARM_VAULT,
    text: "Farm Vault",
    icon: FarmVaultIcon,
    allowedModule: MODULE_FARM_VAULT,
    children: [
      {
        id: "vault_planted_acres",
        text: "Planted Acres",
        path: "/vault",
        allowedModule: MODULE_FARM_VAULT,
      },
      {
        id: "vault_documents",
        text: "Documents",
        path: "/vault/documents",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "vault_farm_potential",
        text: "Farm Potential",
        path: "/vault/potential",
        allowedModule: MODULE_FARM_VAULT,
      },
    ],
  },
  {
    id: MODULE_BUDGET,
    text: "Budgets",
    icon: BudgetIcon,
    path: "/budget",
    allowedModule: MODULE_BUDGET,
  },
  {
    id: MODULE_ARC_PLC,
    text: "ARC/PLC",
    icon: ArcPlcIcon,
    allowedModule: MODULE_ARC_PLC,
    children: [
      {
        id: "arcplc_counties",
        text: "Counties",
        path: "/arcplc",
        allowedModule: MODULE_ARC_PLC,
      },
      {
        id: "arcplc_payments",
        text: "Payments",
        path: "/arcplc/payments",
        allowedModule: MODULE_ARC_PLC,
      },
    ],
  },
  {
    id: MODULE_INSURANCE,
    text: "Insurance",
    icon: InsuranceIcon,
    allowedModule: MODULE_INSURANCE,
    children: [
      {
        id: "insurance_scenarios",
        text: "Scenarios",
        path: "/insurance",
        allowedModule: MODULE_INSURANCE,
      },
      {
        id: "insurance_claims",
        text: "Claims",
        path: "/insurance/claims",
        allowedModule: MODULE_INSURANCE,
      },
    ],
  },
  {
    id: MODULE_FINANCIAL,
    text: "Financial Analysis",
    icon: FinancialIcon,
    allowedModule: MODULE_FINANCIAL,
    children: [
      {
        id: "financial_interview",
        text: "Interview",
        path: "/financial/interview",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_equipment",
        text: "Equipment Inventory",
        path: "/financial/equipment",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_documents",
        text: "Documents",
        path: "/financial/documents",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_balance_sheet",
        text: "Balance Sheet",
        path: "/financial/balance_sheet",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_income_statement",
        text: "Income Statement",
        path: "/financial/income_statement",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_schedule_f",
        text: "Schedule F",
        path: "/financial/schedule_f",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_schedule_f_report",
        text: "Schedule F Report",
        path: "/financial/schedule_f_report",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_debt_schedule",
        text: "Debt Schedule",
        path: "/financial/debt_schedule",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_summary",
        text: "Summary",
        path: "/financial/summary",
        allowedModule: MODULE_FINANCIAL,
      },
      {
        id: "financial_scorecard",
        text: "Score Card",
        path: "/financial/scorecard",
        allowedModule: MODULE_FINANCIAL,
      },
    ],
  },
  {
    id: MODULE_MARKETING,
    text: "Marketing",
    icon: MarketingIcon,
    allowedModule: MODULE_MARKETING,
    children: [
      {
        id: "marketing_sales",
        text: "Sales",
        path: "/marketing/sales",
        allowedModule: MODULE_MARKETING,
      },
      {
        id: "marketing_position_summary",
        text: "Position Summary",
        path: "/marketing/position_summary",
        allowedModule: MODULE_MARKETING,
      },
      {
        id: "marketing_crop_profiles",
        text: "Crop Profiles",
        path: "/marketing/crop_profiles/",
        allowedModule: MODULE_MARKETING,
      },
    ],
  },
  {
    id: MODULE_BROKER,
    text: "Broker",
    icon: BrokerIcon,
    allowedModule: null, // Broker section has special logic
    condition: (globalState) => globalState.is_broker || globalState.user_role === USER_ROLE_ADMIN,
    isBrokerMenuAllowed: (globalState) =>
      (globalState.is_broker && !globalState?.producer_token) || globalState.user_role === USER_ROLE_ADMIN,
    children: [
      {
        id: "broker_transactions",
        text: "Transactions",
        path: "/broker/transactions",
        allowedModule: null, // Special logic applies
      },
      {
        id: "broker_position_summary",
        text: "Position Summary",
        path: "/broker/position_summary",
        allowedModule: null, // Special logic applies
      },
      {
        id: "broker_clients",
        text: "Clients",
        path: "/broker/clients",
        allowedModule: true, // Always allowed if Broker section is visible
      },
      {
        id: "broker_sales",
        text: "Cash Sales",
        path: "/broker/sales",
        allowedModule: true, // Always allowed if Broker section is visible
      },
    ],
  },
  {
    id: MODULE_REPORTS,
    text: "Reports",
    icon: ReportsIcon, // Using FinancialIcon as in original code
    allowedModule: MODULE_REPORTS,
    children: [
      {
        id: "reports_revenue_dashboard",
        text: "Revenue Dashboard",
        path: "/reports/revenue-dashboard",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_yield_forecast",
        text: "Yield Forecast",
        path: "/reports/yield-forecast",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "stress_tests",
        text: "Stress Tests",
        path: "/reports/vault/stress-tests",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_farms_report",
        text: "Planted Acres by Farm",
        path: "/reports/vault/FarmsReport",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_farm_tract_field_report",
        text: "Planted Acres by Field",
        path: "/reports/vault/FarmTractFieldReport",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_base_acres_report",
        text: "Base Acres",
        path: "/reports/vault/BaseAcresReport",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_acres_map",
        text: "Acres Map",
        path: "/reports/vault/AcresMapReport",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_budget_summary",
        text: "Budget Summary",
        path: "/reports/vault/BudgetSummaryReport",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_insurance_summary",
        text: "Insurance Summary",
        path: "/reports/vault/InsuranceSummaryReport",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_claims",
        text: "Insurance Claims",
        path: "/reports/vault/ClaimsReport",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_claims_analysis",
        text: "Claims Analysis",
        path: "/reports/vault/ClaimsAnalysis",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_income_statement",
        text: "Income Statement Report",
        path: "/reports/vault/IncomeStatement",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_balance_sheet",
        text: "Balance Sheet Report",
        path: "/reports/vault/BalanceSheet",
        allowedModule: MODULE_REPORTS,
      },
      {
        id: "reports_producer_advisory",
        text: "Producer Advisory Report",
        path: "/reports/vault/ProducerAdvisory",
        allowedModule: MODULE_REPORTS,
      },
    ],
  },
  {
    id: MODULE_CALCULATORS,
    text: "Calculators",
    icon: CalculatorIcon,
    allowedModule: MODULE_CALCULATORS,
    children: [
      // {
      //   id: "calculators_arc_plc_projection",
      //   text: "ARC/PLC Projection",
      //   path: "/calculators/arc-plc-projection",
      //   allowedModule: MODULE_CALCULATORS,
      // },
      {
        id: "calculators_soybean_scn_risk",
        text: "Soybean SCN Risk",
        path: "/calculators/soybean-scn-risk",
        allowedModule: MODULE_CALCULATORS,
      },
    ],
  },
  {
    id: "lender_loan_origination",
    text: "Loan Origination",
    icon: LoanOriginationIcon,
    path: "/lender/loan_origination_memo",
    allowedModule: MODULE_FINANCIAL,
    condition: (globalState) => globalState?.is_lender || globalState.user_role === USER_ROLE_ADMIN,
  },
  {
    id: "sharing",
    text: "Sharing",
    icon: SecurityIcon,
    path: (globalState) => `/userGrant/${globalState.producer_token}`,
    allowedModule: true, // Permission check is done via disabled logic
    disabledCheck: (globalState) =>
      !([USER_ROLE_PRODUCER, USER_ROLE_ADMIN].includes(globalState.user_role) && Boolean(globalState?.producer_token)),
  },
  {
    id: MODULE_SYSTEM,
    text: "System",
    icon: SystemIcon,
    allowedModule: true, // No specific module check, just role check
    condition: (globalState) => globalState.user_role === USER_ROLE_ADMIN || globalState.is_broker,
    children: [
      {
        id: "system_change_log",
        text: "Change Log",
        path: "/system/change_audit",
        allowedModule: true,
        condition: (globalState) => globalState.user_role === USER_ROLE_ADMIN,
      },
      {
        id: "agencies",
        text: "Agencies",
        path: "/system/agencies",
        allowedModule: true,
      },
      {
        id: "aip_pass",
        text: "AIP PASS",
        path: "/system/aip-pass",
        allowedModule: true,
        condition: (gs) => gs.user_role === USER_ROLE_ADMIN,
      },
    ],
  },
  {
    id: MODULE_TILLEY,
    text: "Hey, Tilley chat",
    icon: TilleyIcon,
    path: "/tilley",
    allowedModule: true,
  },
];

// Separate item for Members, rendered outside the main loop maybe
export const membersMenuItem = {
  id: "members",
  text: "Members",
  icon: MemberManagementIcon,
  path: "/account",
  condition: (globalState) =>
    [USER_ROLE_ADMIN, USER_ROLE_AGENT].includes(globalState.user_role) && !globalState.is_broker,
};
